import { type AssignableProjectRole, CSRF_HEADER, type ProjectPerson } from "@esi/shared";
import { api } from "../../api.ts";
import { noContent, ok } from "../../api-errors.ts";

/** What can hold a role in a project: a person, a directory group or an attribute rule. */
export type AccessKind = "person" | "group" | "rule";

type ByKind<Args extends unknown[]> = Record<AccessKind, (...args: Args) => Promise<unknown>>;

/** Add, change the role of, and remove each kind; the answers' bodies are not needed. */
export interface AccessApi {
  add: ByKind<[target: string, role: AssignableProjectRole]>;
  setRole: ByKind<[id: string, role: AssignableProjectRole]>;
  remove: ByKind<[id: string]>;
  /**
   * Finding people to add (project member managers only): absent where the routes have no
   * search, such as governance, which adds people by e-mail.
   */
  people?: {
    search: (query: string) => Promise<ProjectPerson[]>;
    add: (userId: string, role: AssignableProjectRole) => Promise<unknown>;
  };
}

/**
 * The project endpoints per kind, in one place: add (a person by e-mail, a group or rule by
 * id), change the role, remove; and the people search, whose picks are added by user ID.
 * Every call carries the session's CSRF token.
 */
export function accessApi(projectId: string, csrfToken: string): AccessApi {
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  const param = { projectId };
  const members = api.api.projects[":projectId"].members;
  const rules = api.api.projects[":projectId"]["rule-grants"][":ruleId"];
  const setRule = async (ruleId: string, role: AssignableProjectRole) =>
    ok(await rules.$put({ param: { ...param, ruleId }, json: { role } }, headers));
  return {
    people: {
      search: async (q: string) => ok(await members.search.$get({ param, query: { q } })),
      add: async (userId: string, role: AssignableProjectRole) =>
        ok(await members.$post({ param, json: { userId, role } }, headers)),
    },
    add: {
      person: async (email: string, role: AssignableProjectRole) =>
        ok(await members.$post({ param, json: { email, role } }, headers)),
      group: async (groupId: string, role: AssignableProjectRole) =>
        ok(await members.groups.$post({ param, json: { groupId, role } }, headers)),
      rule: setRule,
    },
    setRole: {
      person: async (userId: string, role: AssignableProjectRole) =>
        ok(await members[":userId"].$put({ param: { ...param, userId }, json: { role } }, headers)),
      group: async (groupId: string, role: AssignableProjectRole) =>
        ok(
          await members.groups[":groupId"].$put(
            { param: { ...param, groupId }, json: { role } },
            headers,
          ),
        ),
      rule: setRule,
    },
    remove: {
      person: async (userId: string) =>
        noContent(await members[":userId"].$delete({ param: { ...param, userId } }, headers)),
      group: async (groupId: string) =>
        noContent(
          await members.groups[":groupId"].$delete({ param: { ...param, groupId } }, headers),
        ),
      rule: async (ruleId: string) =>
        noContent(await rules.$delete({ param: { ...param, ruleId } }, headers)),
    },
  };
}
